import { ValidationError } from "sequelize"
import {
    QidProduct,
    QidProductCreate,
    QidProductSave,
    QidProductChangeStatus,
    MtDriveFolderCreate,
    MtDriveFile,
} from "server/models"
import { PROVEN_NO, PROVEN_IN_USE, PRIOR_USE, PUBLISHED, UNPUBLISHED } from "const"
import { OrderType, PublishedUnpublished, SilDeviceFilter } from "client/utils"
import {
    GET, POST, DELETE, sort, compare, search, validate,
} from "client/store/utils"
import {
    LangType,
    SilProjectDeviceUpdateByFilters,
    SilProjectDeviceClearUpdateByFilters,
    SilDeviceUpdateRequired,
    SilProjectDeviceUpdateRequired,
    CreateDriveFolder,
    LoadDriveFileById,
    LoadDriveFolderByQidProductId,
    MoveDriveFile,
} from "client/store"
import {
    QidProductList,
    QidProductFilters,
    QidProductValidation,
} from "./types"


const CreateProductList = (products: QidProduct[], onlyActive = false) => {
    const productList: QidProductList = {}
    products.forEach((product) => {
        if (onlyActive && product.status === UNPUBLISHED) return
        productList[product.id] = product
    })
    return productList
}

export const LoadAllQidProducts = (onlyActive = false) => (
    GET("/qidProduct/all")
    .then((products: QidProduct[]) => (
        CreateProductList(products, onlyActive)
    ))
)

// get all published products with published models
export const LoadAllQidProductsWithModels = () => (
    GET("/qidProduct/allWithModels")
    .then((products: QidProduct[]) => (
        CreateProductList(products)
    ))
)

export const LoadCompanyQidProducts = (companyId: number, onlyActive = false) => (
    GET(`/qidProduct/companyId/${companyId}`)
    .then((products: QidProduct[]) => (
        CreateProductList(products, onlyActive)
    ))
)

export const LoadQidProductById = (productId: number) => (
    GET(`/qidProduct/id/${productId}`)
    .then((product: QidProduct) => product)
)

export const SaveQidProduct = (
    id: number,
    product: QidProductCreate,
    accountId: number,
    updated: boolean,
    lang: LangType,
) => {
    const data: QidProductSave = {
        id,
        product,
    }
    return POST(
        "/qidProduct/save",
        JSON.stringify(data),
    ).then((res) => {
        const newProduct = res as QidProduct
        const newProductId = newProduct.id || null
        const error = res as ValidationError
        const validation = <QidProductValidation>validate(error, lang)

        const result = {
            newProductId,
            productFolderId: 0,
            validation,
        }

        if (validation) return result

        const promiseArray: Promise<any>[] = []

        if (!validation && id && updated) {
            // update SIL devices
            promiseArray.push(
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: id,
                }),
            )
        }

        if (newProductId) {
            const productFolder: MtDriveFolderCreate = {
                folderId: 0,
                prevFolderId: null,
                accountId,
                projectId: null,
                qidProductId: newProductId,
                name: "QID Product Folder",
                copy: 0,
            }

            promiseArray.push(
                // create product folder and move uploaded logo
                CreateDriveFolder(productFolder, lang).then(({ newFolder }) => {
                    if (newFolder) result.productFolderId = newFolder.id
                    if (newFolder && product.logoId) {
                        return LoadDriveFileById(product.logoId).then((file) => {
                            const moveFile: MtDriveFile = {
                                ...file,
                                folderId: newFolder.id,
                            }
                            return MoveDriveFile(moveFile)
                        })
                    }
                    return null
                }),
            )
        }

        if (id && product.logoId) {
            promiseArray.push(
                // move selected logo
                LoadDriveFolderByQidProductId(id).then((folder) => {
                    LoadDriveFileById(product.logoId).then((file) => {
                        const moveFile: MtDriveFile = {
                            ...file,
                            folderId: folder.id,
                        }
                        return MoveDriveFile(moveFile)
                    })
                }),
            )
        }

        return Promise.all(promiseArray).then(() => result)
    })
}

export const ChangeQidProductStatus = (product: QidProduct) => {
    const status: PublishedUnpublished = product.status === PUBLISHED ? UNPUBLISHED : PUBLISHED
    const data: QidProductChangeStatus = {
        id: product.id,
        status,
    }
    return POST(
        "/qidProduct/changeStatus",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
            ? status
            : product.status
    ))
}

export const DeleteQidProduct = (productId: number) => (
    DELETE(`/qidProduct/delete/${productId}`)
    .then((removedRows: number) => {
        if (removedRows === 1) {
            // update SIL devices - clear "updated" mark
            return UpdateQidDependingDevices({
                update: false,
                qidProductId: productId,
            }).then(() => removedRows)
        }

        return removedRows
    })
)


export const SortQidProducts = (
    products: readonly QidProduct[],
    sortColumn: keyof QidProduct,
    sortOrder: OrderType,
    filters: QidProductFilters,
): number[] => {
    const productsFiltered = products.filter((product) => {
        let showRow = true
        if (filters.companyId && product.companyId !== filters.companyId) showRow = false
        else if (filters.name && product.name.toUpperCase().indexOf(filters.name.toUpperCase()) < 0) showRow = false
        else if (filters.type && product.type !== filters.type) showRow = false

        else if (filters.lowDemandMode && product.lowDemandMode !== filters.lowDemandMode) showRow = false
        else if (filters.highDemandMode && product.highDemandMode !== filters.highDemandMode) showRow = false
        else if (filters.continuousMode && product.continuousMode !== filters.continuousMode) showRow = false

        else if (
            (filters.logicSolver === false || filters.logicSolver === true)
            && product.logicSolver !== filters.logicSolver
        ) showRow = false
        else if (
            [PROVEN_NO, PROVEN_IN_USE, PRIOR_USE].indexOf(filters.provenInUse) >= 0
            && product.provenInUse !== filters.provenInUse
        ) showRow = false

        else if (filters.status && product.status !== filters.status) showRow = false
        else if (filters.search) showRow = search(filters.search, product)
        return showRow
    })

    if (sortColumn === "company") {
        productsFiltered.sort((a, b) => {
            const A = a.company.name.toUpperCase()
            const B = b.company.name.toUpperCase()
            return compare(A, B, sortOrder)
        })
    } else {
        productsFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))
    }

    const productIds: number[] = []
    productsFiltered.forEach((product) => productIds.push(product.id))

    return productIds
}


// update depending project devices when QID devices are changed
interface UpdateDependingProps {
    update: boolean
    qidProductId?: number
    qidModelId?: number
    qidSafetyFunctionId?: number
    qidOptionId?: number
    qidDatapointId?: number
}
export const UpdateQidDependingDevices = ({
    update,
    qidProductId,
    qidModelId,
    qidSafetyFunctionId,
    qidOptionId,
    qidDatapointId,
}: UpdateDependingProps) => {
    const promiseArray: Promise<any>[] = []
    const filters: SilDeviceFilter = {
        qidProductId,
        qidModelId,
        qidSafetyFunctionId,
        qidOptionId,
        qidDatapointId,
    }

    // update project devices
    if (update) {
        promiseArray.push(SilProjectDeviceUpdateByFilters(filters))
    } else {
        promiseArray.push(SilProjectDeviceClearUpdateByFilters(filters))
    }

    // update subsystems
    promiseArray.push(
        SilDeviceUpdateRequired(filters, update),
        SilProjectDeviceUpdateRequired(filters, update),
    )

    return Promise.all(promiseArray)
}
